import EF from 'ef/ef';
import 'initialize';
import 'lib/view-helper';
import CourseSettingsService from 'services/course-handler-services/course-settings-service';
import PortfolioService from 'services/data-handler-services/portfolio-service';
import NavigationService from 'services/course-handler-services/navigation-service';
import IconLoadService from 'services/loader-handler-services/icon-load-service';
import utils from 'lib/utils';
import ServiceInitializer from 'services/services-initializer';
import CustomHbsPartials from 'views/partials';
import { timerService } from 'services/course-handler-services/timer-service';
import CourseTimerService from 'services/timer/course-timer-service';
import Customization from 'ef/models/customization';
import ModulesCollection from 'ef/collections/modules_collection';
import Chaplin from 'chaplin';
import $ from 'jquery';
import _ from 'lodash';
import i18next from 'i18next';
import Handlebars from 'handlebars/runtime';
import Controllers from 'controllers';

export default Chaplin.Application.extend({
  title: 'EverFi SDK',
  initialize: function (options) {
    Chaplin.Dispatcher.prototype.loadController = function (name, handler) {
      const moduleName = `${_.startCase(name.split('/').pop())}Controller`;
      return setTimeout(() => handler(Controllers[moduleName]));
    };

    const params = new URLSearchParams(window.location.search);
    const urlOptions = params.get('options') ? JSON.parse(atob(params.get('options'))) : {};
    _.defaultsDeep(options, urlOptions, utils.tryRequire('local-data/config.json'), {
      config: {
        settings: {
          local: true,
          env: 'dev',
          // fullSCORM: true // left as an example
        },
        enrollment: {
          id: 1,
          name: 'Charles Xavier',
          schoolName: 'EverFi Institute',
          className: 'Spring 2013',
          certified: false,
          autoPlay: false,
        },
        i18n: {},
      },
    });

    // HR config passed here
    const config = _.defaultsDeep(options.config, {
      learnerProfile: { ...options.learnerProfile },
    });
    this.config = config;
    if (this.config.i18n.lngs) {
      this.config.settings.available_languages = this.config.i18n.lngs;
    }
    if (window.location.search.indexOf('setLng') >= 0) {
      this.config.i18n.lng = null;
    }
    // if the config decides to send the app elsewhere replace the hash now
    // this prevents the router from routing to the menu and then to the page
    // if the menu was loaded and then quickly switched to another page
    // the menu would dispose itself before it was done loading and cause some undefined errors
    if (this.config && this.config.other && this.config.other.start) {
      window.location.replace(`#${this.config.other.start}`);
    }

    this.setRequestHeaders();
    utils.initialize(this.config.settings);
    NavigationService.initialize(this.config.settings);
    Chaplin.Application.prototype.initialize.apply(this, arguments);
  },
  start: function () {
    this.setCSRFToken();

    ServiceInitializer.start(this.config);
    EF.on('ready', () => {
      const args = Array.prototype.slice.call(arguments);
      if (CourseSettingsService.get('portfolio')) {
        PortfolioService.initialize();
      }
      if (EF.options.local) {
        EF.activityProgresses.fetch();
        EF.customizations.fetch();
      }

      let customModules = EF.customizations.findByType('content-module-customization');
      if (!customModules && this.config.settings.module_keys) {
        const attributes = Object.assign(
          { customization_type: 'content-module-customization' },
          this.config.settings.module_keys
        );
        const options = { enrollment: this.config.enrollment };

        customModules = new Customization(attributes, options);
        EF.customizations.add(customModules);
      }
      if (customModules) {
        const newModules = EF.modules.filter(mod => customModules.get('modules').indexOf(mod.get('key')) > -1);
        customModules.get('modules').forEach((mod, i) => {
          const module = EF.modules.find({ key: mod });
          if (module) {
            module.set('index', i);
          } else {
            // eslint-disable-next-line no-console
            console.log(`couldn't find module with key ${mod} in `, EF.modules);
          }
        });
        EF.modules = new ModulesCollection(newModules);
        EF.modules.sort();
        customModules.set('force_order', customModules.get('force_order') === 'true');
        EF.groups.each(g => {
          g.modules.sort();
          g.modules.forEach(m => {
            if (m && m.get('key') !== undefined && EF.modules.find({ key: m.get('key') }) === undefined) {
              // Inconsistency, this module is in groups but not in EF.modules
              // Get rid of it so it doesn't get in the way of unlocking the next module
              g.modules.remove(m);
            }
          });
          if (g.get('custom_in_order')) {
            g.set('in_order', customModules.get('force_order'));
          }
        });
      }

      Chaplin.Application.prototype.start.apply(this, args);
      const startUpScript = CourseSettingsService.get('startupScript');
      if (startUpScript) {
        import(startUpScript);
      }
      if (CourseSettingsService.get('menu') && CourseSettingsService.get('menu').skip && !window.location.hash) {
        let firstIncomplete = EF.modules.getFirstAvailable(CourseSettingsService.get('menu'));
        if (!firstIncomplete) {
          firstIncomplete = EF.modules.first();
        }
        this.router.route({ url: firstIncomplete.get('key') }, { replace: true });
      }

      if (CourseSettingsService.get('defaultConfigs')) {
        CourseSettingsService.setDefaultConfigs();
      }

      // Register hbs partials
      this.registerPartials();

      IconLoadService.configFontAwesome({
        autoReplaceSvg: false,
        observeMutations: false,
        showMissingIcons: false,
      });

      EF.timerService = timerService;
      EF.courseTimerService = CourseTimerService.initialize();
    });

    this.onOrientationChange();

    this.router.subscribeEvent('router:match', this.trackPageView);
    this.router.subscribeEvent('router:match', this.postMessageNewUrl);
  },

  trackPageView: function (route) {
    if (window.ga && typeof window.ga.getAll === 'function') {
      const url = `${location.pathname}#${route.path}`;
      window.ga.getAll().forEach(tracker => {
        tracker.send('pageview', url.replace(/^!/, ''));
      });
    }
  },
  postMessageNewUrl(route) {
    window.postMessage(
      {
        event_type: 'url-change',
        new_url: `${location.origin}/${location.pathname}#${route.path}`,
      },
      window.location.origin
    );
  },
  registerPartials() {
    Object.entries(CustomHbsPartials).forEach(([name, partial]) => {
      Handlebars.registerPartial(name, partial);
    });
  },
  setCSRFToken: function () {
    const funcs = {
      csrfToken: function () {
        return $('meta[name=csrf-token]').attr('content');
      },

      // Make sure that every Ajax request sends the CSRF token
      CSRFProtection: function (xhr) {
        const token = funcs.csrfToken();
        if (token) {
          xhr.setRequestHeader('X-CSRF-Token', token);
        }
      },
    };

    $.ajaxPrefilter(function (options, originalOptions, xhr) {
      if (!options.crossDomain) {
        funcs.CSRFProtection(xhr);
      }
    });
  },
  setRequestHeaders: function () {
    $.ajaxSetup({
      beforeSend: function (xhr) {
        xhr.setRequestHeader('accept', 'application/json');
        if (i18next.language) {
          // also see app/extentions for the fetch shim
          xhr.setRequestHeader('Accept-Language', `${i18next.language};q=1.0`);
        }
      },
    });
  },
  onOrientationChange: function () {
    if (window.addEventListener && EF && EF.config) {
      if (window.top !== window) {
        window.addEventListener(
          'message',
          function (message) {
            const originMatch = message.origin === EF.config.get('basePath');
            if (originMatch && message.data.type === 'orientationchange') {
              EF.orientation = message.data.orientation;
              EF.trigger('orientationchange', message.data.orientation);
            }
          },
          false
        );
      } else {
        window.addEventListener(
          'orientationchange',
          function () {
            EF.orientation = window.orientation;
            EF.trigger('orientationchange', window.orientation);
          },
          false
        );
      }
    }
  },
  // listens for window focus changing
  // if activeElement is an iframe, it'll treat the window like it has focus
  onWindowFocusChange(callback) {
    let visible = true;

    const focused = () => {
      if (!visible) {
        callback((visible = true));
      }
    };
    const unfocused = () => {
      if (visible) {
        callback((visible = false));
      }
    };
    const activeElement = window.document.activeElement;
    if (activeElement && activeElement.tagName.toLowerCase() === 'iframe') {
      focused;
    }
    if ('hidden' in document) {
      document.addEventListener('visibilitychange', () => {
        document.hidden ? unfocused : focused;
      });
    }
    if ('mozHidden' in document) {
      document.addEventListener('mozvisibilitychange', () => {
        document.mozHidden ? unfocused : focused;
      });
    }
    if ('webkitHidden' in document) {
      document.addEventListener('webkitvisibilitychange', () => {
        document.webkitHidden ? unfocused : focused;
      });
    }
    if ('msHidden' in document) {
      document.addEventListener('msvisibilitychange', () => {
        document.msHidden ? unfocused : focused;
      });
    }
    // IE 9 and lower:
    if ('onfocusin' in document) {
      document.onfocusin = focused;
      document.onfocusout = unfocused;
    }
    // All others:
    window.onpageshow = window.onfocus = focused;
    window.onpagehide = window.onblur = unfocused;
  },
});
